import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'
import AdminLayout from '@/components/layout/AdminLayout'
import MediaUploadZone from '@/components/admin/MediaUploadZone'
import NfcManagementCard from '@/components/admin/NfcManagementCard'


interface JerseyFormData {
  playerName: string
  jerseyNumber: string
  team: string
  size: string
  condition: string
  gameTitle: string
  gameDate: string
  opponent: string
  venue: string
  stats: {
    touchdowns: string
    passingYards: string
    completions: string
  }
  description: string
  mediaIds: string[]
  nfcUid: string
}

const TEAMS = [
  { label: 'New England Patriots', value: 'patriots' },
  { label: 'Tampa Bay Buccaneers', value: 'buccaneers' },
  { label: 'Kansas City Chiefs', value: 'chiefs' },
  // Add more teams as needed
]

const SIZES = [
  { label: 'XS', value: 'xs' },
  { label: 'S', value: 's' },
  { label: 'M', value: 'm' },
  { label: 'L', value: 'l' },
  { label: 'XL', value: 'xl' },
  { label: 'XXL', value: 'xxl' },
]

const CONDITIONS = [
  { label: 'New', value: 'new' },
  { label: 'Like New', value: 'like-new' },
  { label: 'Good', value: 'good' },
  { label: 'Fair', value: 'fair' },
]

export default function AdminDashboardPage() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'Add Item' | 'NFC Tags' | 'Manage'>('Add Item')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')
  const [isNfcBound, setIsNfcBound] = useState(false)
  const [currentListingId, setCurrentListingId] = useState<string | undefined>()
  
  // For demo purposes, simulate listing creation
  const simulateListingCreation = () => {
    setCurrentListingId('demo-listing-' + Date.now())
  }

  const [formData, setFormData] = useState<JerseyFormData>({
    playerName: '',
    jerseyNumber: '',
    team: '',
    size: '',
    condition: '',
    gameTitle: '',
    gameDate: '',
    opponent: '',
    venue: '',
    stats: {
      touchdowns: '',
      passingYards: '',
      completions: ''
    },
    description: '',
    mediaIds: [],
    nfcUid: ''
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    
    if (name.startsWith('stats.')) {
      const statName = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        stats: {
          ...prev.stats,
          [statName]: value
        }
      }))
    } else {
      setFormData(prev => ({ ...prev, [name]: value }))
    }
  }

  const handleMediaUploaded = (mediaIds: string[]) => {
    setFormData(prev => ({
      ...prev,
      mediaIds
    }))
  }

  const handleNfcBound = (uid: string) => {
    setFormData(prev => ({ ...prev, nfcUid: uid }))
  }

  const handleNfcBindingStateChange = (isBound: boolean) => {
    setIsNfcBound(isBound)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setSuccess('')

    try {
      if (formData.mediaIds.length === 0) {
        setError('At least one media file is required.')
        return
      }

      // For demo purposes, simulate listing creation
      console.log('Creating jersey with data:', formData)
      
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      // Simulate successful creation
      simulateListingCreation()
      setSuccess('Listing saved successfully! Now you can bind an NFC tag and publish.')
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create jersey')
    } finally {
      setLoading(false)
    }
  }

  const handlePublish = async () => {
    if (!currentListingId) {
      setError('Listing must be created before publishing')
      return
    }

    if (!isNfcBound) {
      setError('NFC tag must be bound before publishing')
      return
    }

    try {
      setLoading(true)
      setError('')
      
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      // Simulate successful publish
      setSuccess('Listing published successfully and is now LIVE!')
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to publish listing')
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('authToken')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('user')
    navigate('/admin/login')
  }

  return (
    <AdminLayout onLogout={handleLogout}>
      <div className="min-h-screen" style={{ backgroundColor: 'var(--background-secondary)' }}>
        {/* Header */}
        <div className="border-b" style={{ 
          backgroundColor: 'var(--surface-primary)', 
          borderColor: 'var(--border-primary)',
          padding: 'var(--spacing-lg) var(--spacing-xl)'
        }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center" style={{ gap: 'var(--spacing-md)' }}>
              <button
                onClick={() => navigate('/')}
                className="flex items-center transition-colors duration-150 hover-text-primary"
                style={{ 
                  gap: 'var(--spacing-sm)', 
                  color: 'var(--text-secondary)'
                }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>
            </div>
            
            <div className="flex items-center" style={{ gap: 'var(--spacing-sm)' }}>
              <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>certi</h1>
              <span className="px-3 py-1 text-xs font-medium rounded-full" style={{
                backgroundColor: 'var(--brand-primary)',
                color: 'var(--text-inverse)'
              }}>
                Admin
              </span>
            </div>
            
            <div className="w-16"></div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto" style={{ padding: 'var(--spacing-2xl)' }}>
          {/* Hero Section with Metrics */}
          <div className="hero-gradient card shimmer" style={{ padding: 'var(--spacing-2xl)', marginBottom: 'var(--spacing-2xl)' }}>
            <div className="text-center" style={{ marginBottom: 'var(--spacing-xl)' }}>
              <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto floating-animation pulse-glow" style={{
                background: 'linear-gradient(135deg, var(--brand-primary) 0%, #0a5230 100%)',
                marginBottom: 'var(--spacing-lg)'
              }}>
                <svg className="w-10 h-10" style={{ color: 'white' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                </svg>
              </div>
              <h1 className="text-4xl font-bold gradient-text" style={{ marginBottom: 'var(--spacing-sm)' }}>
                Admin Dashboard
              </h1>
              <p className="text-lg" style={{ color: 'var(--text-secondary)', opacity: 0.8 }}>
                Jersey Creation & NFC Management Platform
              </p>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="metric-card" style={{ 
                padding: 'var(--spacing-lg)',
                borderRadius: 'var(--radius-lg)',
                textAlign: 'center'
              }}>
                <div className="text-2xl font-bold gradient-text">247</div>
                <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>Total Items</div>
              </div>
              <div className="metric-card" style={{ 
                padding: 'var(--spacing-lg)',
                borderRadius: 'var(--radius-lg)',
                textAlign: 'center'
              }}>
                <div className="text-2xl font-bold" style={{ color: 'var(--accent-success)' }}>98%</div>
                <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>NFC Success</div>
              </div>
              <div className="metric-card" style={{ 
                padding: 'var(--spacing-lg)',
                borderRadius: 'var(--radius-lg)',
                textAlign: 'center'
              }}>
                <div className="text-2xl font-bold" style={{ color: 'var(--brand-primary)' }}>156</div>
                <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>Live Items</div>
              </div>
              <div className="metric-card" style={{ 
                padding: 'var(--spacing-lg)',
                borderRadius: 'var(--radius-lg)',
                textAlign: 'center'
              }}>
                <div className="text-2xl font-bold" style={{ color: 'var(--accent-warning)' }}>23</div>
                <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>Pending</div>
              </div>
            </div>
          </div>


          {/* Premium Tab Navigation */}
          <div className="card" style={{ marginBottom: 'var(--spacing-2xl)' }}>
            <div style={{ padding: 'var(--spacing-md)' }}>
              <nav className="flex" style={{ gap: 'var(--spacing-sm)' }}>
                {['Add Item', 'NFC Tags', 'Manage'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab as any)}
                    className="font-medium text-sm transition-all duration-300"
                    style={{
                      padding: 'var(--spacing-md) var(--spacing-lg)',
                      borderRadius: 'var(--radius-md)',
                      background: activeTab === tab 
                        ? 'linear-gradient(135deg, var(--brand-primary) 0%, #0a5230 100%)'
                        : 'transparent',
                      color: activeTab === tab ? 'white' : 'var(--text-muted)',
                      boxShadow: activeTab === tab 
                        ? '0 4px 15px rgba(11, 77, 42, 0.3)' 
                        : '0 2px 8px rgba(0, 0, 0, 0.02)',
                      transform: activeTab === tab ? 'translateY(-1px)' : 'translateY(0)'
                    }}
                  >
                    {tab}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div style={{ padding: 'var(--spacing-xl)' }}>
              {activeTab === 'Add Item' && (
                <div>
                  <div className="flex items-center mb-8" style={{ gap: 'var(--spacing-md)' }}>
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{
                      background: 'linear-gradient(135deg, var(--brand-primary) 0%, #0a5230 100%)',
                      boxShadow: '0 4px 15px rgba(11, 77, 42, 0.2)'
                    }}>
                      <svg className="w-6 h-6" style={{ color: 'white' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold gradient-text">Create New Jersey</h3>
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Fill in the details below to create an authenticated jersey listing</p>
                    </div>
                  </div>

                  {success && (
                    <div className="mb-8 shimmer" style={{ 
                      padding: 'var(--spacing-lg)', 
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)',
                      border: '1px solid rgba(16, 185, 129, 0.2)',
                      borderRadius: 'var(--radius-lg)',
                      backdropFilter: 'blur(10px)',
                      boxShadow: '0 4px 20px rgba(16, 185, 129, 0.15)'
                    }}>
                      <div className="flex items-center" style={{ gap: 'var(--spacing-md)' }}>
                        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ 
                          background: 'var(--accent-success)',
                          color: 'white'
                        }}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <p className="font-medium" style={{ color: 'var(--accent-success)' }}>{success}</p>
                      </div>
                    </div>
                  )}

                  {error && (
                    <div className="mb-8 shimmer" style={{ 
                      padding: 'var(--spacing-lg)', 
                      background: 'linear-gradient(135deg, rgba(225, 29, 72, 0.1) 0%, rgba(225, 29, 72, 0.05) 100%)',
                      border: '1px solid rgba(225, 29, 72, 0.2)',
                      borderRadius: 'var(--radius-lg)',
                      backdropFilter: 'blur(10px)',
                      boxShadow: '0 4px 20px rgba(225, 29, 72, 0.15)'
                    }}>
                      <div className="flex items-center" style={{ gap: 'var(--spacing-md)' }}>
                        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ 
                          background: 'var(--accent-danger)',
                          color: 'white'
                        }}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </div>
                        <p className="font-medium" style={{ color: 'var(--accent-danger)' }}>{error}</p>
                      </div>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-8">
                    {/* NFC Tag Management */}
                    <div className="glass-card" style={{ 
                      padding: 'var(--spacing-lg)', 
                      borderRadius: 'var(--radius-lg)',
                      marginBottom: 'var(--spacing-lg)'
                    }}>
                      <NfcManagementCard
                        listingId={currentListingId}
                        currentNfcUid={formData.nfcUid || undefined}
                        onNfcBound={handleNfcBound}
                        onBindingStateChange={handleNfcBindingStateChange}
                      />
                    </div>

                    {/* Basic Information Section */}
                    <div className="glass-card" style={{ 
                      padding: 'var(--spacing-lg)', 
                      borderRadius: 'var(--radius-lg)'
                    }}>
                      <div className="flex items-center mb-6" style={{ gap: 'var(--spacing-md)' }}>
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{
                          background: 'linear-gradient(135deg, var(--brand-primary) 0%, #0a5230 100%)'
                        }}>
                          <svg className="w-4 h-4" style={{ color: 'white' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <h4 className="text-lg font-semibold gradient-text">Basic Information</h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          name="playerName"
                          placeholder="Player Name"
                          value={formData.playerName}
                          onChange={handleChange}
                          required
                        />
                        <Input
                          name="jerseyNumber"
                          type="number"
                          placeholder="Jersey Number"
                          value={formData.jerseyNumber}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      
                      <div className="mt-4">
                        <select
                          name="team"
                          value={formData.team}
                          onChange={handleChange}
                          required
                          className="input"
                        >
                          <option value="">Select Team</option>
                          {TEAMS.map(team => (
                            <option key={team.value} value={team.value}>{team.label}</option>
                          ))}
                        </select>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <select
                          name="size"
                          value={formData.size}
                          onChange={handleChange}
                          required
                          className="input"
                        >
                          <option value="">Select Size</option>
                          {SIZES.map(size => (
                            <option key={size.value} value={size.value}>{size.label}</option>
                          ))}
                        </select>

                        <select
                          name="condition"
                          value={formData.condition}
                          onChange={handleChange}
                          required
                          className="input"
                        >
                          <option value="">Select Condition</option>
                          {CONDITIONS.map(condition => (
                            <option key={condition.value} value={condition.value}>{condition.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Game Information Section */}
                    <div className="glass-card" style={{ 
                      padding: 'var(--spacing-lg)', 
                      borderRadius: 'var(--radius-lg)'
                    }}>
                      <div className="flex items-center mb-6" style={{ gap: 'var(--spacing-md)' }}>
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{
                          background: 'linear-gradient(135deg, var(--brand-primary) 0%, #0a5230 100%)'
                        }}>
                          <svg className="w-4 h-4" style={{ color: 'white' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                          </svg>
                        </div>
                        <h4 className="text-lg font-semibold gradient-text">Game Information</h4>
                      </div>
                      <div className="space-y-4">
                        <Input
                          name="gameTitle"
                          placeholder="Game Title"
                          value={formData.gameTitle}
                          onChange={handleChange}
                        />
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <Input
                            name="gameDate"
                            type="date"
                            value={formData.gameDate}
                            onChange={handleChange}
                          />
                          <Input
                            name="opponent"
                            placeholder="Opponent"
                            value={formData.opponent}
                            onChange={handleChange}
                          />
                        </div>

                        <Input
                          name="venue"
                          placeholder="Venue"
                          value={formData.venue}
                          onChange={handleChange}
                        />
                      </div>
                    </div>

                    {/* Player Stats Section */}
                    <div className="glass-card" style={{ 
                      padding: 'var(--spacing-lg)', 
                      borderRadius: 'var(--radius-lg)'
                    }}>
                      <div className="flex items-center mb-6" style={{ gap: 'var(--spacing-md)' }}>
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{
                          background: 'linear-gradient(135deg, var(--accent-success) 0%, #059669 100%)'
                        }}>
                          <svg className="w-4 h-4" style={{ color: 'white' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                        </div>
                        <h4 className="text-lg font-semibold" style={{ color: 'var(--accent-success)' }}>Player Stats (Optional)</h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Input
                          name="stats.touchdowns"
                          type="number"
                          placeholder="Touchdowns"
                          value={formData.stats.touchdowns}
                          onChange={handleChange}
                        />
                        <Input
                          name="stats.passingYards"
                          type="number"
                          placeholder="Passing Yards"
                          value={formData.stats.passingYards}
                          onChange={handleChange}
                        />
                        <Input
                          name="stats.completions"
                          type="number"
                          placeholder="Completions"
                          value={formData.stats.completions}
                          onChange={handleChange}
                        />
                      </div>
                    </div>

                    {/* Description Section */}
                    <div className="glass-card" style={{ 
                      padding: 'var(--spacing-lg)', 
                      borderRadius: 'var(--radius-lg)'
                    }}>
                      <div className="flex items-center mb-6" style={{ gap: 'var(--spacing-md)' }}>
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{
                          background: 'linear-gradient(135deg, var(--accent-warning) 0%, #d97706 100%)'
                        }}>
                          <svg className="w-4 h-4" style={{ color: 'white' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </div>
                        <h4 className="text-lg font-semibold" style={{ color: 'var(--accent-warning)' }}>Description</h4>
                      </div>
                      <textarea
                        name="description"
                        placeholder="Describe the significance of this item, game details, and any special notes..."
                        value={formData.description}
                        onChange={handleChange}
                        rows={4}
                        className="input"
                        style={{ resize: 'vertical', minHeight: '120px' }}
                      />
                    </div>

                    {/* Media Upload Section */}
                    <div className="glass-card" style={{ 
                      padding: 'var(--spacing-lg)', 
                      borderRadius: 'var(--radius-lg)'
                    }}>
                      <div className="flex items-center mb-6" style={{ gap: 'var(--spacing-md)' }}>
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{
                          background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)'
                        }}>
                          <svg className="w-4 h-4" style={{ color: 'white' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold" style={{ color: '#8B5CF6' }}>Media Upload</h4>
                          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                            Upload images and videos of the jersey. You can drag to reorder files.
                          </p>
                        </div>
                      </div>
                      <MediaUploadZone
                        onMediaUploaded={handleMediaUploaded}
                        maxFiles={8}
                      />
                      {formData.mediaIds.length > 0 && (
                        <div className="mt-4 p-3 rounded-lg" style={{
                          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)',
                          border: '1px solid rgba(16, 185, 129, 0.2)'
                        }}>
                          <p className="text-sm font-medium" style={{ color: 'var(--accent-success)' }}>
                            ✓ {formData.mediaIds.length} file{formData.mediaIds.length === 1 ? '' : 's'} uploaded successfully
                          </p>
                        </div>
                      )}
                    </div>

                    {/* NFC Tag Management (also available near submit) */}
                    {currentListingId && (
                      <div className="glass-card" style={{ 
                        padding: 'var(--spacing-lg)', 
                        borderRadius: 'var(--radius-lg)',
                        marginBottom: 'var(--spacing-lg)'
                      }}>
                        <NfcManagementCard
                          listingId={currentListingId}
                          currentNfcUid={formData.nfcUid || undefined}
                          onNfcBound={handleNfcBound}
                          onBindingStateChange={handleNfcBindingStateChange}
                        />
                      </div>
                    )}

                    <div className="flex flex-col sm:flex-row gap-4">
                      <Button
                        type="submit"
                        disabled={loading}
                        className="flex-1"
                      >
                        {loading ? 'Saving...' : 'Save Listing'}
                      </Button>
                      
                      <Button
                        type="button"
                        onClick={handlePublish}
                        disabled={loading || !currentListingId || !isNfcBound}
                        className="flex-1"
                        style={{
                          backgroundColor: loading || !currentListingId || !isNfcBound 
                            ? 'var(--gray-400)' 
                            : 'var(--accent-success)',
                          color: 'var(--text-inverse)'
                        }}
                      >
                        {loading ? 'Publishing...' : 'Publish to LIVE'}
                      </Button>
                    </div>
                    
                    {/* NFC Validation Message */}
                    {currentListingId && !isNfcBound && (
                      <div className="border" style={{ 
                        backgroundColor: 'color-mix(in srgb, var(--accent-warning) 5%, transparent)',
                        borderColor: 'color-mix(in srgb, var(--accent-warning) 20%, transparent)',
                        borderRadius: 'var(--radius-md)',
                        padding: 'var(--spacing-md)'
                      }}>
                        <div className="flex items-center">
                          <svg className="w-5 h-5 mr-2" style={{ color: 'var(--accent-warning)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                          </svg>
                          <p className="text-sm" style={{ color: 'color-mix(in srgb, var(--accent-warning) 80%, var(--text-primary))' }}>
                            <strong>NFC tag required:</strong> Bind an NFC tag before publishing to LIVE status
                          </p>
                        </div>
                      </div>
                    )}
                    
                    {/* Success Message for LIVE */}
                    {currentListingId && isNfcBound && (
                      <div className="border" style={{ 
                        backgroundColor: 'color-mix(in srgb, var(--accent-success) 5%, transparent)',
                        borderColor: 'color-mix(in srgb, var(--accent-success) 20%, transparent)',
                        borderRadius: 'var(--radius-md)',
                        padding: 'var(--spacing-md)'
                      }}>
                        <div className="flex items-center">
                          <svg className="w-5 h-5 mr-2" style={{ color: 'var(--accent-success)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-sm" style={{ color: 'color-mix(in srgb, var(--accent-success) 80%, var(--text-primary))' }}>
                            <strong>Ready to publish:</strong> NFC tag is bound, listing can go LIVE
                          </p>
                        </div>
                      </div>
                    )}
                  </form>
                </div>
              )}

              {activeTab === 'NFC Tags' && (
                <div className="text-center" style={{ padding: 'var(--spacing-3xl) 0' }}>
                  <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto floating-animation" style={{
                    background: 'linear-gradient(135deg, var(--brand-primary) 0%, #0a5230 100%)',
                    marginBottom: 'var(--spacing-xl)',
                    boxShadow: '0 8px 25px rgba(11, 77, 42, 0.3)'
                  }}>
                    <svg className="w-12 h-12" style={{ color: 'white' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold gradient-text mb-4">NFC Tag Management</h3>
                  <p className="text-lg mb-8" style={{ color: 'var(--text-secondary)', maxWidth: '500px', margin: '0 auto' }}>
                    NFC functionality is optimized for mobile devices. Access the full NFC management suite through our mobile application.
                  </p>
                  <div className="flex flex-col items-center" style={{ gap: 'var(--spacing-md)' }}>
                    <Button 
                      onClick={() => window.open('/mobile', '_blank')}
                      className="shimmer"
                      style={{ padding: 'var(--spacing-md) var(--spacing-xl)' }}
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      Open Mobile App
                    </Button>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                      Scan, bind, and manage NFC tags with advanced mobile features
                    </p>
                  </div>
                </div>
              )}

              {activeTab === 'Manage' && (
                <div>
                  <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold gradient-text mb-2">Management Tools</h3>
                    <p style={{ color: 'var(--text-secondary)' }}>Advanced administrative functions and analytics</p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <button
                      onClick={() => navigate('/admin/analytics')}
                      className="glass-card shimmer transition-all duration-300 hover:scale-105 group"
                      style={{ 
                        padding: 'var(--spacing-xl)',
                        textAlign: 'left',
                        position: 'relative'
                      }}
                    >
                      <div className="flex items-start" style={{ gap: 'var(--spacing-md)' }}>
                        <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{
                          background: 'linear-gradient(135deg, var(--brand-primary) 0%, #0a5230 100%)',
                          boxShadow: '0 4px 15px rgba(11, 77, 42, 0.3)'
                        }}>
                          <svg className="w-7 h-7" style={{ color: 'white' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-bold text-lg gradient-text mb-1">Analytics Dashboard</p>
                          <p className="text-sm" style={{ color: 'var(--text-secondary)', lineHeight: '1.4' }}>
                            View performance metrics, user engagement, and sales analytics
                          </p>
                          <div className="flex items-center mt-2" style={{ gap: 'var(--spacing-xs)' }}>
                            <div className="w-2 h-2 rounded-full" style={{ background: 'var(--accent-success)' }}></div>
                            <span className="text-xs" style={{ color: 'var(--accent-success)' }}>Live Data</span>
                          </div>
                        </div>
                      </div>
                    </button>

                    <button
                      onClick={() => navigate('/admin/bulk-import')}
                      className="glass-card shimmer transition-all duration-300 hover:scale-105 group"
                      style={{ 
                        padding: 'var(--spacing-xl)',
                        textAlign: 'left',
                        position: 'relative'
                      }}
                    >
                      <div className="flex items-start" style={{ gap: 'var(--spacing-md)' }}>
                        <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{
                          background: 'linear-gradient(135deg, var(--accent-success) 0%, #059669 100%)',
                          boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)'
                        }}>
                          <svg className="w-7 h-7" style={{ color: 'white' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-bold text-lg" style={{ color: 'var(--accent-success)' }}>Bulk Import</p>
                          <p className="text-sm" style={{ color: 'var(--text-secondary)', lineHeight: '1.4' }}>
                            Import multiple jersey items from CSV or Excel files
                          </p>
                          <div className="flex items-center mt-2" style={{ gap: 'var(--spacing-xs)' }}>
                            <div className="w-2 h-2 rounded-full" style={{ background: 'var(--accent-warning)' }}></div>
                            <span className="text-xs" style={{ color: 'var(--accent-warning)' }}>Beta Feature</span>
                          </div>
                        </div>
                      </div>
                    </button>

                    <button
                      onClick={() => navigate('/marketplace')}
                      className="glass-card shimmer transition-all duration-300 hover:scale-105 group"
                      style={{ 
                        padding: 'var(--spacing-xl)',
                        textAlign: 'left',
                        position: 'relative'
                      }}
                    >
                      <div className="flex items-start" style={{ gap: 'var(--spacing-md)' }}>
                        <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{
                          background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
                          boxShadow: '0 4px 15px rgba(139, 92, 246, 0.3)'
                        }}>
                          <svg className="w-7 h-7" style={{ color: 'white' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-bold text-lg" style={{ color: '#8B5CF6' }}>View Marketplace</p>
                          <p className="text-sm" style={{ color: 'var(--text-secondary)', lineHeight: '1.4' }}>
                            Preview how your items appear to customers
                          </p>
                          <div className="flex items-center mt-2" style={{ gap: 'var(--spacing-xs)' }}>
                            <div className="w-2 h-2 rounded-full" style={{ background: 'var(--brand-primary)' }}></div>
                            <span className="text-xs" style={{ color: 'var(--brand-primary)' }}>Public View</span>
                          </div>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}